import { Box, ChakraProvider, Divider, Flex, Heading, Stack, Text } from "@chakra-ui/react"
import Moment from "moment"
import React from "react"
import { useParams } from "react-router-dom"
import { Employee } from "types/Employee"
import ErrorWrapper from "../../../components/ErrorWrapper"
import Exoskeleton from "../../../components/Exoskeleton"
import { PlanSteps } from "./plan-steps"
import { RoutingInfoDetail } from "components/routinginfo/RoutingInfoDetail"
import { useViewCarrotPlan } from "components/employees/hooks/useViewCarrotPlan"
import { Alert, Typography } from "@carrotfertility/carotene-core"
import { theme } from "../../../utils/theme"

type PlanProps = {
  employee: Employee
}
export default function Plan({ employee }: PlanProps): JSX.Element {
  const { planId } = useParams<{ planId: string }>()
  const {
    carrotPlan,
    carrotPlanStepMenuItems,
    matchingRoutingInfo,
    pastRoutingInfo,
    couldRoutingInfoHaveBeenUsedAlready,
    areAnyLoading,
    carrotPlanError,
    menuItemsError
  } = useViewCarrotPlan(planId, employee.employeeId)

  const planDate = carrotPlan && Moment(carrotPlan.createdAtUtc).format("MMM DD")
  const republishedPreviousPlan = carrotPlan && (carrotPlan.sourceCarrotPlanId ? "(republished previous plan)" : null)

  return (
    <ChakraProvider theme={theme}>
      <ErrorWrapper errors={[carrotPlanError, menuItemsError]}>
        <Exoskeleton isLoading={areAnyLoading}>
          <Flex flexDirection="column">
            <Box mb="32px">
              <Heading size="lg" fontWeight="light">
                Carrot Plan - {planDate} {republishedPreviousPlan}
              </Heading>
              {carrotPlan?.createdByUsername && <Text mt={"8px"}>Published by: {carrotPlan?.createdByUsername}</Text>}
            </Box>
            <PlanSteps carrotPlanStepMenuItems={carrotPlanStepMenuItems} employee={employee} />
            <Box mt="48px" mb="20px">
              <Heading fontWeight="normal" fontSize="18px">
                About this Carrot Plan
              </Heading>
            </Box>
            <Stack direction="column" spacing="4">
              <Box>
                <Heading size="sm">Journey</Heading>
                <Stack spacing="0">
                  {carrotPlan?.journeys?.slice(0, 3).map((journey, index) => {
                    const labels = ["Primary", "Secondary", "Tertiary"]
                    return (
                      <Text key={index}>
                        {journey?.journeyDisplayName && `${labels[index]}: ${journey.journeyDisplayName}`}
                      </Text>
                    )
                  })}
                </Stack>
              </Box>
              {carrotPlan?.internalNote && (
                <Box>
                  <Heading size="sm">Internal note</Heading>
                  <Text>{carrotPlan.internalNote}</Text>
                </Box>
              )}
              <Divider />
              {couldRoutingInfoHaveBeenUsedAlready ? (
                <Alert severity={"warning"} variant={"filled"} sx={{ background: "#fdf5b0" }}>
                  <Typography variant={"body1"} fontStyle={"italic"}>
                    {"This routing info may have been used for a previous Carrot Plan."}
                  </Typography>
                </Alert>
              ) : null}
              {matchingRoutingInfo && (
                <>
                  <Heading fontWeight="normal" fontSize="18px" paddingBottom="1.5rem">
                    Routing Questions
                  </Heading>
                  <RoutingInfoDetail
                    currentRoutingInfo={matchingRoutingInfo}
                    employee={employee}
                    pastRoutingInfo={pastRoutingInfo}
                    showAboutYou={false}
                    showPastRoutingAnswers={false}
                  />
                </>
              )}
            </Stack>
          </Flex>
        </Exoskeleton>
      </ErrorWrapper>
    </ChakraProvider>
  )
}
